import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import generateId from 'src/lib/generateId';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import audiencesResource, { Audience } from 'src/actions/audiences/audiencesResource';
import analytics from '@ad-scripts/lib/analytics';

const dataAidPrefix = 'admin-audience-';

const AudienceEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const add = audiencesResource.effects.add;
  const update = audiencesResource.effects.update;
  const validate = audiencesResource.effects.validate;

  const formContext = useForm({
    add,
    defaultItem: { name: '' },
    errors,
    item: item
      ? {
          ...item,
          updatedAt: undefined,
          updatedBy: undefined,
        }
      : null,
    resetErrors: audiencesResource.actions.resetErrors,
    root,
    update,
  });

  const { onClose, isNew } = formContext;

  const onSave = useOnSave({ ...formContext, add, update, validate });

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Create' : 'Edit'} Audience</H1>

      <Body data-aid={`${dataAidPrefix}editor`}>
        <Description>
          An audience is used to organize and segment Answers into a particular grouping. For example, an audience can
          be used to segment Answers by business lines, brands, or regions.
        </Description>
        <Section>
          <Row>
            <Input
              data-aid={`${dataAidPrefix}editor-label-input`}
              attr="name"
              autoFocus
              label="Name"
              placeholder="Name"
            />
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-submit`} onClick={onSave}>
          {isNew ? 'Create Audience' : 'Save'}
        </Button>
      </Footer>
    </Form>
  );
};

function useOnSave({ add, isNew, item, update, validate, workingState }) {
  const dispatch = useDispatch();
  return useCallback(() => {
    const id = isNew ? generateId() : workingState.id;
    const formattedName = workingState.name.trim();
    const draft = {
      ...workingState,
      id,
      name: formattedName,
    };
    if (!dispatch(validate(draft, Audience))) return;
    if (isNew) {
      analytics.track('Audience Created', { id, name: formattedName });
      dispatch(add({}, draft));
    } else {
      analytics.track('Audience Updated', { id, name: formattedName });
      dispatch(update({ id: item.id }, draft));
    }
  }, [add, dispatch, isNew, item, update, validate, workingState]);
}

const Description = styled.div`
  margin-bottom: 20px;
  font-size: 13px;
  color: #767676;
`;

export default AudienceEditor;
